import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Empty, Popconfirm, Select, Space, Spin, Table, Tag, Tooltip, Typography, notification,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import pmApi from './pmApi';
import PlanBandPriceModal, { fmtMoney } from './PlanBandPriceModal';
import {
  PREMIUM_TYPE_ORDER, PREMIUM_TYPE_LABEL, pricingModeSummary, subBandSubtitle,
} from '../../components/PlanManagement/premiumTypes';

const { Text } = Typography;

/**
 * Plan-level Bands & Pricing (Plans Catalogue update, Oct 2026).
 *
 * Everything about ONE plan's offer in one place: the bands attached to it,
 * each band's price, change price, add a band from the master list, detach,
 * and delete. All edits happen here; nothing navigates away.
 *
 * The general Bands & Pricing tab stays the master setup — new sub-bands,
 * renames, age ranges, archive — and is not replaced by this view. Band facts
 * (name, members, ages) are therefore read-only here.
 *
 * Detach = remove the band from THIS plan (soft; price history kept).
 * Delete  = remove the band from the master list for every plan. The API only
 *           allows it for a band that was never priced or purchased anywhere,
 *           so it appears only when it could succeed.
 */
export default function PlanBandsPanel({ planId, plan, onChanged, compact = false }) {
  const [bands, setBands] = useState([]);
  const [planBands, setPlanBands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [priceTarget, setPriceTarget] = useState(null); // { band, current }
  const [addBandId, setAddBandId] = useState(null);
  const [busyBandId, setBusyBandId] = useState(null);

  const load = useCallback(async () => {
    if (!planId) return;
    setLoading(true);
    const [b, pb] = await Promise.all([pmApi.bands(), pmApi.planBands(planId)]);
    if (b.ok) setBands(b.data?.bands || []);
    if (pb.ok) setPlanBands(pb.data?.bands || []);
    setLoading(false);
  }, [planId]);

  useEffect(() => { load(); }, [load]);

  const changed = useCallback(() => { load(); onChanged?.(); }, [load, onChanged]);

  const bandById = useMemo(() => {
    const m = {};
    bands.forEach((b) => { m[Number(b.id)] = b; });
    return m;
  }, [bands]);

  // Attached = pm_plan_bands rows with status 1, in master-list order.
  const attached = useMemo(() => {
    const active = planBands.filter((pb) => Number(pb.status) === 1);
    const order = (pb) => {
      const t = PREMIUM_TYPE_ORDER.indexOf(pb.premium_type);
      return [t === -1 ? 99 : t, pb.max_members == null ? 1 : 0, Number(pb.max_members || 0), Number(pb.min_age || 0)];
    };
    return active.sort((a, b) => {
      const oa = order(a); const ob = order(b);
      for (let i = 0; i < oa.length; i += 1) if (oa[i] !== ob[i]) return oa[i] - ob[i];
      return 0;
    });
  }, [planBands]);

  const attachedIds = useMemo(() => new Set(attached.map((pb) => Number(pb.band_id))), [attached]);

  // Addable = active master bands not yet on this plan.
  const addable = useMemo(
    () => bands.filter((b) => Number(b.status) === 1 && !attachedIds.has(Number(b.id))),
    [bands, attachedIds],
  );

  const detach = async (pb) => {
    setBusyBandId(pb.band_id);
    const r = await pmApi.detachBand(planId, pb.band_id);
    setBusyBandId(null);
    if (r.ok) {
      notification.success({ message: 'Band detached', description: r.message });
      changed();
    }
  };

  const deleteBand = async (bandId) => {
    setBusyBandId(bandId);
    const r = await pmApi.deleteBand(bandId);
    setBusyBandId(null);
    if (r.ok) {
      notification.success({ message: 'Band deleted', description: r.message });
      changed();
    }
  };

  const columns = [
    {
      title: 'Band',
      key: 'band',
      render: (_, pb) => {
        const band = bandById[Number(pb.band_id)] || pb;
        return (
          <div>
            <b>{pb.band_name || band.name}</b>
            <div className="pm-benefit-sub">
              <Tag style={{ marginInlineEnd: 6 }}>{PREMIUM_TYPE_LABEL[pb.premium_type] || pb.premium_type}</Tag>
              {subBandSubtitle({ ...band, ...pb, name: pb.band_name || band.name })}
              {Number(pb.is_default_dependent) === 1 ? <> · <span className="pm-chip pm-chip-blue">default dependent</span></> : null}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Price',
      key: 'price',
      width: 220,
      render: (_, pb) => (
        <Space size={8} wrap>
          <b style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(pb.effective_price ?? pb.current_price)}</b>
          {pb.premium_type === 'dependent' && pb.pricing_mode && pb.pricing_mode !== 'fixed' ? (
            <span className="pm-chip pm-chip-muted">{pricingModeSummary(pb)}</span>
          ) : null}
          {Number(pb.org_override_count) > 0 ? (
            <Tooltip title="Organizations with a negotiated price on this band — those win over this list price.">
              <span className="pm-chip pm-chip-blue">{pb.org_override_count} org override{Number(pb.org_override_count) > 1 ? 's' : ''}</span>
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
    ...(compact ? [] : [{
      title: 'Price history',
      key: 'history',
      width: 240,
      render: (_, pb) => {
        const history = pb.price_history || [];
        if (!history.length) return <Text type="secondary">—</Text>;
        return (
          <div style={{ fontSize: 11.5, color: 'var(--pm-ink-faint, #8c8c8c)' }}>
            {history.slice(0, 3).map((h, i) => (
              <div key={i} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {fmtMoney(h.price)} · {String(h.effective_from).slice(0, 10)}
                {h.effective_to ? ` → ${String(h.effective_to).slice(0, 10)}` : ' → now'}
              </div>
            ))}
          </div>
        );
      },
    }]),
    {
      title: '',
      key: 'actions',
      width: 260,
      align: 'right',
      render: (_, pb) => {
        const band = bandById[Number(pb.band_id)];
        const busy = busyBandId === pb.band_id;
        // Delete only when the API could allow it: not Individual, never sold.
        // "Used by this plan" is itself a use, so a band must be detached first.
        const canDelete = band && band.code !== 'individual' && Number(band.used_by_clients) === 0;
        return (
          <Space size={4}>
            <Button size="small" onClick={() => setPriceTarget({ band: band || pb, current: pb })}>
              Change price
            </Button>
            <Popconfirm
              title="Detach this band from the plan?"
              description="It stops being offered on this plan. Price history is kept and the band stays on the master list."
              okText="Detach"
              onConfirm={() => detach(pb)}
            >
              <Button size="small" danger type="text" loading={busy}>Detach</Button>
            </Popconfirm>
            {canDelete ? (
              <Tooltip title="Deletes the band from the master list, for every plan. Only possible while it has never been priced or purchased anywhere — detach it from this plan first.">
                <Button size="small" danger type="text" disabled>Delete</Button>
              </Tooltip>
            ) : null}
          </Space>
        );
      },
    },
  ];

  if (!planId) return <Empty description="Pick a plan" />;
  if (loading && !planBands.length && !bands.length) {
    return <div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>;
  }

  const addableOptions = PREMIUM_TYPE_ORDER
    .map((type) => ({
      label: PREMIUM_TYPE_LABEL[type],
      options: addable.filter((b) => b.premium_type === type).map((b) => ({
        value: b.id,
        label: `${b.name}${b.max_members ? ` · ${b.max_members} member${Number(b.max_members) === 1 ? '' : 's'}` : ''}`,
      })),
    }))
    .filter((g) => g.options.length);

  const deletable = addable.filter((b) => b.code !== 'individual' && Number(b.used_by_clients) === 0
    && !planBands.some((pb) => Number(pb.band_id) === Number(b.id)));

  return (
    <div>
      <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }} wrap align="start">
        <Space wrap>
          <Select
            showSearch
            optionFilterProp="label"
            style={{ minWidth: 260 }}
            placeholder={addable.length ? 'Add a band to this plan…' : 'Every active band is already on this plan'}
            value={addBandId}
            onChange={setAddBandId}
            options={addableOptions}
            disabled={!addable.length}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            disabled={!addBandId}
            onClick={() => {
              const band = bandById[Number(addBandId)];
              if (band) setPriceTarget({ band, current: null });
            }}
          >
            Add & set price
          </Button>
        </Space>
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
      </Space>

      {attached.length ? (
        <Table size="small" rowKey="band_id" columns={columns} dataSource={attached} pagination={false} />
      ) : (
        <Empty
          description={`No bands on ${plan?.plan_name || 'this plan'} yet — add one above to price it.`}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}

      {deletable.length ? (
        <details style={{ marginTop: 16 }}>
          <summary className="pm-benefit-sub" style={{ cursor: 'pointer' }}>
            Delete unused bands from the master list ({deletable.length})
          </summary>
          <Alert
            type="warning"
            showIcon
            style={{ margin: '8px 0' }}
            message="Deleting removes the band for every plan"
            description="Only bands that have never been priced on any plan or sold to anyone are listed. Everything else is archived from the general Bands & Pricing setup instead."
          />
          <Space wrap>
            {deletable.map((b) => (
              <Popconfirm
                key={b.id}
                title={`Delete "${b.name}" from the master list?`}
                okText="Delete"
                okButtonProps={{ danger: true }}
                onConfirm={() => deleteBand(b.id)}
              >
                <Button size="small" danger loading={busyBandId === b.id}>{b.name}</Button>
              </Popconfirm>
            ))}
          </Space>
        </details>
      ) : null}

      <PlanBandPriceModal
        planId={planId}
        target={priceTarget}
        onClose={() => setPriceTarget(null)}
        onSaved={() => { setAddBandId(null); changed(); }}
      />
    </div>
  );
}
